import React, { useCallback, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { ModalConfirmAction } from "components/modals/ModalConfirmAction"

import { TagsFiltersBar } from "features/tags/filters/TagsFiltersBar"
import { TagsTable } from "features/tags/tables/TagsTable"

import { useDebouncedEffect } from "hooks/useDebouncedEffect"

import { tagsService } from "services/tags/tags.service"
import { TagWithUsage } from "services/tags/type"

const DEBOUNCE_MS = 300

type Props = {
  loading: boolean
  setLoading: (v: boolean) => void
  onCreate: () => void
  onEdit: (tag: TagWithUsage) => void
  onGlobalRefresh: () => Promise<void>
  refreshKey: number
  onManualTagsLoaded?: (rows: TagWithUsage[]) => void
}

export const ManualTagsTab: React.FC<Props> = ({
  loading,
  setLoading,
  refreshKey,
  onCreate,
  onEdit,
  onGlobalRefresh,
  onManualTagsLoaded
}) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [rows, setRows] = useState<TagWithUsage[]>([])

  // filters
  const [query, setQuery] = useState("")
  const [category, setCategory] = useState("")

  // TODO: endpoint for categories
  const categories = useMemo(() => {
    const set = new Set<string>()
    rows.forEach((r) => r.category && set.add(r.category))
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [rows])

  const load = useCallback(async () => {
    try {
      const next = await tagsService.getTagsStats({
        isAutomatic: false,
        q: query || undefined,
        category: category || undefined
      })

      const safe = next ?? []
      setRows(safe)
      onManualTagsLoaded?.(safe)
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to load manual tags")
    }
  }, [query, category, onManualTagsLoaded])

  useDebouncedEffect(
    () => {
      load()
    },
    [load, refreshKey],
    DEBOUNCE_MS
  )

  const onRefreshLocal = useCallback(async () => {
    setLoading(true)
    try {
      await load()
    } finally {
      setLoading(false)
    }
  }, [load, setLoading])

  const [pendingDelete, setPendingDelete] = useState<TagWithUsage | null>(null)

  const deleteDialog = useMemo(() => {
    const count = pendingDelete?.usageCount ?? 0
    const name = pendingDelete?.name ?? ""

    return {
      text: count > 0 ? t("tags.deleteTag.confirmAssigned", { name, count }) : t("tags.deleteTag.confirm", { name }),
      actionText: count > 0 ? t("tags.deleteTag.actionAssigned") : t("tags.deleteTag.action")
    }
  }, [pendingDelete, t])

  const confirmDelete = useCallback(
    async (tag: TagWithUsage) => {
      const usage = tag.usageCount ?? 0

      setLoading(true)
      try {
        // The API refuses an in-use tag unless asked to force it, and the dialog the operator has
        // just confirmed is what constitutes asking.
        const res = await tagsService.deleteTag(tag.id, { force: usage > 0 })
        if (res) {
          const unassigned = res.unassigned ?? 0
          toast.success(unassigned > 0 ? `Tag deleted and removed from ${unassigned} player${unassigned === 1 ? "" : "s"}` : "Tag deleted")
          await onGlobalRefresh()
        }
      } catch (e: any) {
        toast.error(e?.data?.message ?? e?.message ?? "Failed to delete tag")
      } finally {
        setLoading(false)
      }
    },
    [onGlobalRefresh, setLoading]
  )

  return (
    <>
      <TagsFiltersBar
        loading={loading}
        query={query}
        onQuery={setQuery}
        category={category}
        onCategory={setCategory}
        categories={categories}
        onRefresh={onRefreshLocal}
        onCreate={onCreate}
      />

      <TagsTable rows={rows} loading={loading} onEdit={onEdit} onDelete={setPendingDelete} />

      <ModalConfirmAction
        data={pendingDelete}
        handler={() => setPendingDelete(null)}
        text={deleteDialog.text}
        actionText={deleteDialog.actionText}
        actionClassName="btn-error"
        actionHandler={confirmDelete}
      />
    </>
  )
}
